import { createRoot, createEffect } from "solid-js";
import { createStore } from "solid-js/store";
import { validateEvent } from "@/tracking/schema";
import { deepSanitize, sanitizeReferrer, sanitizeText, sanitizeUrl } from "@/tracking/sanitize";
import {
  TRACKING_SCHEMA_VERSION,
  type AuthState,
  type BrandMode,
  type BusinessFlow,
  type EventContext,
  type EventName,
  type EventOf,
  type UserGroup,
} from "@/tracking/types";
import { lockedDealerAccount } from "@/shared/lockedDealer";
import { subscribeToConsent } from "@/tracking/consent/consentStore";
import { DEFAULT_CONSENT, type ConsentSnapshot } from "@/tracking/consent/consentTypes";
import { ensureGtmLoaded, pushConsentUpdate, pushToDataLayer, type ConsentSignals } from "./gtm";
import {
  gtmContainerId,
  setTrackingConfig,
  trackingAllowed,
  trackingConfig,
  trackingEnvironment,
  type TrackingConfig,
} from "./config";

/**
 * The one place an event may reach the data layer. Every call site in the app goes through
 * <c>track</c>; nothing pushes to <c>window.dataLayer</c> directly.
 *
 * <p>Events are dropped, never queued. GTM replays whatever is already in the data layer when the
 * container boots, so an event parked before consent and pushed afterwards is indistinguishable
 * from one collected without permission — the replay is the leak. A visitor who agrees halfway
 * through a session is measured from that point on: one page_view for the page they are standing
 * on, then whatever they do next. What they did before is gone, which is the point.</p>
 *
 * <p>Group A — internal sales on a <c>?token=</c> deep link, and anyone holding Admin or
 * GarageInternal — never pushes, and never causes the container to load at all. The group is
 * unknown until the session resolves, and unknown is treated as group A rather than as a customer:
 * a session load that failed must not be the thing that lets staff traffic into the audiences.</p>
 *
 * <p>What is pushed is the schema's object, not the caller's. Parameters are sanitised first
 * (deepSanitize, by key and by value), then validated (validateEvent, which strips anything the
 * event does not declare and refuses an event that is not in the allowlist). The envelope is
 * applied after both, so a call site cannot overwrite the fields the routing rules depend on.</p>
 */

// ── Context ──────────────────────────────────────────────────────────────────

/**
 * The parts of the envelope that come from the session and the tenant rather than from the event.
 * Set as those stores settle; a later call refines an earlier one rather than replacing it.
 */
export interface TrackingContext {
  /** A (internal, never sent), B (dealer), C (end customer). Null until the session resolves. */
  userGroup: UserGroup | null;
  authState: AuthState;
  /** Tenant id from GET /api/tenant/config — host-resolved server-side, never from the URL. */
  tenantId: string;
  brandMode: BrandMode;
  /** Build identity, so a reporting anomaly can be tied back to a release. */
  appVersion: string;
}

const EMPTY_CONTEXT: TrackingContext = {
  userGroup: null,
  authState: "guest",
  tenantId: "default",
  brandMode: "lobas",
  appVersion: "unknown",
};

const contextStore = createRoot(() => {
  const [state, setState] = createStore<{ context: TrackingContext }>({
    context: { ...EMPTY_CONTEXT },
  });
  return { state, setState };
});

export function setContext(patch: Partial<TrackingContext>): void {
  contextStore.setState("context", (prev) => ({ ...prev, ...patch }));
  // The group can arrive after the decision — a visitor who consented on an earlier visit is
  // already granted when the session is still resolving. Re-run the decision so a legitimate
  // visitor is not left untracked by the order the two happened to settle in.
  applyConsent(consentSignals());
}

/** The current envelope context. Exposed for callers that need to reason about it, and for tests. */
export const trackingContext = (): TrackingContext => contextStore.state.context;

// ── Consent ──────────────────────────────────────────────────────────────────

/**
 * The last snapshot the consent store handed over. Held rather than read from the store, because
 * <c>subscribeToConsent</c> is the store's stated entry point for this layer and it fires once
 * immediately — a visitor who decided on an earlier visit would otherwise wait for a change that
 * never comes.
 */
let consent: ConsentSnapshot = {
  decided: false,
  categories: { ...DEFAULT_CONSENT },
  record: null,
};

const consentSignals = (): ConsentSignals => ({
  statistics: consent.categories.statistics,
  marketing: consent.categories.marketing,
});

/** Neither optional category granted means no container, so nothing behavioural can be pushed. */
const anyOptionalConsent = (): boolean =>
  consent.categories.statistics || consent.categories.marketing;

// ── State ────────────────────────────────────────────────────────────────────

let initialised = false;
let gtmReady = false;

/**
 * Event ids already pushed. Two call sites reaching the same conversion — a double-clicked submit,
 * a success branch that re-runs — would otherwise be counted twice, and a lead counted twice is a
 * lead invented.
 */
const seen = new Set<string>();

/** Bounded, so a long configurator session cannot grow the set without end. */
const SEEN_LIMIT = 500;

function remember(eventId: string): void {
  if (seen.size >= SEEN_LIMIT) {
    // A Set iterates in insertion order, so the first entry is the oldest.
    const oldest = seen.values().next().value;
    if (oldest !== undefined) seen.delete(oldest);
  }
  seen.add(eventId);
}

// ── Business flow ────────────────────────────────────────────────────────────

/**
 * Which funnel an event belongs to. Derived rather than passed in: it is a property of the event
 * name, and asking every call site for it would mean a missing envelope field on the day someone
 * forgets. <c>internal_order</c> is absent by construction — that path is group A, which never
 * reaches this function.
 */
const FLOW_BY_EVENT: Partial<Record<EventName, BusinessFlow>> = {
  page_view: "catalog",
  view_item_list: "catalog",
  select_item: "catalog",
  view_item: "catalog",
  sign_up: "account",
  login: "account",
  logout: "account",
  dealer_list_view: "lead",
  dealer_select: "lead",
  quote_request_start: "lead",
  generate_lead: "lead",
  qualify_lead: "lead",
  working_lead: "lead",
  disqualify_lead: "lead",
  close_convert_lead: "lead",
  close_unconvert_lead: "lead",
  begin_checkout: "webshop_checkout",
  checkout_handoff_error: "webshop_checkout",
  purchase: "webshop_checkout",
  refund: "webshop_checkout",
};

function flowFor(eventName: EventName): BusinessFlow {
  const explicit = FLOW_BY_EVENT[eventName];
  if (explicit !== undefined) return explicit;
  if (eventName.startsWith("configurator_")) return "configurator";
  if (eventName.startsWith("dashboard_")) return "dashboard";
  return "catalog";
}

/**
 * Parameters GA4 reads as an ecommerce payload. Their presence is what makes an event one that has
 * to clear the previous payload first.
 */
const ECOMMERCE_PARAMS = ["items", "transaction_id"] as const;

// ── Helpers ──────────────────────────────────────────────────────────────────

function devLog(message: string, detail?: unknown): void {
  if (!import.meta.env.DEV) return;
  console.debug("[tracking] " + message, detail ?? "");
}

function newEventId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // Insecure origin, or a browser without it. The fallback is fine — this is not a secret.
  }
  return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

/**
 * Whether anything at all may be pushed right now: the container is up, the configuration permits
 * it, the visitor is not staff, and at least one optional category is granted.
 */
function canPush(): boolean {
  if (!gtmReady) return false;
  if (!trackingAllowed()) return false;
  const group = contextStore.state.context.userGroup;
  if (group === null || group === "A") return false;
  return anyOptionalConsent();
}

/** The envelope. Built here, applied last, and therefore not something a call site can dictate. */
function envelope(eventId: string, flow: BusinessFlow): EventContext {
  const ctx = contextStore.state.context;
  return {
    event_id: eventId,
    tracking_schema_version: TRACKING_SCHEMA_VERSION,
    event_timestamp: new Date().toISOString(),
    environment: trackingEnvironment(),
    app_version: ctx.appVersion,
    tenant_id: ctx.tenantId,
    brand_mode: ctx.brandMode,
    // Non-null by canPush(), which refuses an unresolved group outright.
    user_group: ctx.userGroup as UserGroup,
    auth_state: ctx.authState,
    business_flow: flow,
    event_source: "configurator_spa",
    // What the visitor actually agreed to, not what the app currently ships — a receipt written
    // against version 1 stays a version 1 receipt after the text is revised.
    consent_policy_version: String(
      consent.record?.policyVersion ?? trackingConfig().consentPolicyVersion ?? "",
    ),
    // Read per event rather than once: a second store's link opened in the same tab replaces the
    // first. Left out without a lock, never sent as "" — see EventContext.
    ...lockedDealer(),
  };
}

function lockedDealer(): Pick<EventContext, "locked_dealer_id"> {
  const account = lockedDealerAccount();
  return account ? { locked_dealer_id: account } : {};
}

// ── Public surface ───────────────────────────────────────────────────────────

/** The parameters one event accepts — required and optional, with everything else typed never. */
export type EventParams<E extends EventName> = Omit<EventOf<E>, "event" | keyof EventContext>;

export interface TrackOptions {
  /** Supply the id when client and server have to agree on one (Meta CAPI deduplication). */
  eventId?: string;
  /** Override the derived funnel. The backend integration path is the reason this exists. */
  businessFlow?: BusinessFlow;
}

/**
 * Push one event, if every condition holds. Returns whether it was pushed, so a caller can tell
 * "sent" from "silently and correctly dropped".
 */
export function track<E extends EventName>(
  eventName: E,
  params: EventParams<E>,
  options: TrackOptions = {},
): boolean {
  if (!canPush()) {
    devLog('dropped "' + eventName + '" (consent, group or configuration)');
    return false;
  }

  const eventId = options.eventId ?? newEventId();
  if (seen.has(eventId)) {
    devLog('dropped "' + eventName + '" (duplicate event_id ' + eventId + ")");
    return false;
  }

  // Sanitise before the schema runs: deepSanitize can carry off a required parameter, and
  // validateEvent is what notices that it did.
  const clean = deepSanitize(params as Record<string, unknown>);

  const candidate = {
    event: eventName,
    ...clean,
    ...envelope(eventId, options.businessFlow ?? flowFor(eventName)),
  };

  const validated = validateEvent(candidate);
  if (validated === null) return false;

  // Only now is the event certain to be pushed — remembering it earlier would let one rejected
  // event block the corrected retry that follows it.
  remember(eventId);

  const cleared = validated as unknown as Record<string, unknown>;
  if (ECOMMERCE_PARAMS.some((param) => cleared[param] !== undefined)) resetEcommerce();

  pushToDataLayer(cleared);
  return true;
}

/**
 * Clear the ecommerce object. Pushed automatically before any event that carries an ecommerce
 * payload; exported for a caller that needs to clear it on its own when a flow is abandoned.
 *
 * <p>The data layer is cumulative — GTM merges each push over what it already holds — so items from
 * a previous event survive into the next one unless the object is nulled between them. That is how
 * a purchase comes to report the basket from the configuration before it.</p>
 */
export function resetEcommerce(): boolean {
  if (!canPush()) return false;
  pushToDataLayer({ ecommerce: null });
  return true;
}

/**
 * A page_view for an SPA route change. The router changes the URL without a document load, so
 * nothing else states which page the visitor is on.
 *
 * <p>The URL is put through <c>sanitizeUrl</c> before it travels. The sales deep link arrives as
 * <c>?token=…</c> and resolves an Internal session — it is a credential, and GA4 is not a place a
 * credential can be recalled from.</p>
 */
export function trackPageView(path?: string, title?: string): boolean {
  if (typeof window === "undefined") return false;

  let href = window.location.href;
  if (path !== undefined) {
    try {
      href = new URL(path, window.location.origin).href;
    } catch {
      devLog("page path could not be resolved to a URL", path);
      return false;
    }
  }

  const params: Record<string, unknown> = {
    page_location: sanitizeUrl(href),
    page_title: sanitizeText(title ?? (typeof document === "undefined" ? "" : document.title)),
  };

  const referrer = typeof document === "undefined" ? "" : sanitizeReferrer(document.referrer);
  if (referrer) params.page_referrer = referrer;

  return track("page_view", params as EventParams<"page_view">);
}

/**
 * Act on the current consent state: bring the container up when it is allowed, report a change to
 * one already up, and state the current page the first time it comes up.
 */
export function applyConsent(signals: ConsentSignals): void {
  const containerId = gtmContainerId();
  if (containerId === null || !trackingAllowed()) return;

  // Group A does not merely stop at the data layer: the container is never fetched for them, so no
  // third-party script runs and no cookie is written on a staff member's browser.
  const group = contextStore.state.context.userGroup;
  if (group === null || group === "A") return;

  const wasReady = gtmReady;
  gtmReady = ensureGtmLoaded(containerId, signals);
  if (!gtmReady) return;

  if (wasReady) {
    // Already loaded — a choice widened or narrowed from the settings panel. Withdrawal travels too.
    pushConsentUpdate(signals);
    return;
  }

  // The container has just come up, part-way through a session on a route it was not present for.
  // One page_view states where the visitor actually is; everything before consent stays unsent.
  // This branch runs only on the transition, so it cannot repeat.
  trackPageView();
}

/**
 * Wire the tracking layer up. Safe to call once, from app start.
 *
 * <p><c>subscribeToConsent</c> fires immediately with the stored decision, so a returning visitor
 * who agreed on an earlier visit takes the same path as one deciding now.</p>
 *
 * <p>Returns the teardown. The app itself never needs it — the layer lives as long as the document
 * does — but a subscription with no way off the list is a leak by construction, and it is the thing
 * that makes this module testable in isolation.</p>
 */
export function initTracking(config?: TrackingConfig | null): () => void {
  if (config !== undefined) setTrackingConfig(config);
  if (initialised) return () => {};
  initialised = true;

  const unsubscribe = subscribeToConsent((snapshot) => {
    consent = snapshot;
    applyConsent(consentSignals());
  });

  // The tenant configuration and the session both settle after this runs, and either can be the one
  // to settle last. applyConsent reads both stores, so running it inside an effect re-runs the
  // decision whenever one of them moves. Without it a returning visitor whose consent was already
  // on file would be stranded by a tenant config that arrived a moment later — the subscription had
  // already fired, and nothing would ask again.
  const dispose = createRoot((disposeRoot) => {
    createEffect(() => applyConsent(consentSignals()));
    return disposeRoot;
  });

  return () => {
    unsubscribe();
    dispose();
    initialised = false;
  };
}
